/**
 * Navigation contracts — slice 15, "the Reach shell": the sidebar's shape.
 * Slice 65 regrouped it around four pillars; see "Reach and Connect" below.
 *
 * ## What this is, and what it deliberately is not
 *
 * This file is a **structure, not a payload**. No endpoint returns it and
 * nothing parses it off the wire; the sidebar is built in the browser from the
 * features already on `SessionUser`. It lives in `packages/shared` anyway
 * because the vocabulary it is made of — the product pillars and what may be
 * said about each — is already here in `./overview.js`, and the one thing
 * worth preventing is a second, subtly different answer to "is Flow a thing
 * yet" appearing next to the first.
 *
 * It carries **no labels, no icons and no routes.** Those are the web app's:
 * `AppLayout.tsx` owns the words and `App.tsx` owns the paths, and a package
 * the API imports has no business knowing that Audiences lives at
 * `/audiences`. What crosses is the grouping and the state. Because
 * {@link NAV_SECTION_VALUES} is an enum, the web app's map from section to
 * `{ label, icon, path }` should be typed `Record<NavSectionId, …>` — then a
 * section added here without a route fails to compile instead of rendering a
 * gap.
 *
 * ## The sidebar was not nine flat items
 *
 * The brief for this slice describes nine flat entries. That was true of an
 * earlier `AppLayout.tsx` and is not true of the one this file was written
 * against: it already has three sections — an unlabelled Overview, **Reach**
 * (Audiences, Segments, Templates, Campaigns) and **Workspace** (Integrations,
 * Credits, Analytics, Reports). Its header argues at length why Analytics and
 * Reports sit under Workspace rather than under Reach — they answer questions
 * about a *period*, asked after the fact, and placing them beside Campaigns
 * would read as steps in getting a message out of the door.
 *
 * That argument is still right, so this file keeps it rather than flattening
 * eight sections under one pillar to make the grouping look tidier. Since slice
 * 65 the shape has four kinds of thing:
 *
 *   - `home` — the Overview. Above the groups, belonging to no pillar, because
 *     it is where the pillars are *listed*.
 *   - `pillars` — one group per {@link FEATURE_VALUES} member, in
 *     {@link PILLAR_ORDER}, including Flow, which does not exist.
 *   - `shared` — Audiences, Segments, Templates. The layers every pillar draws
 *     on; see below.
 *   - `workspace` — Integrations, Credits, Analytics, Reports. **Not a
 *     pillar**, and given no pillar state: these are the workspace's own
 *     machinery, they do not become a copy per pillar as pillars ship, and a
 *     tenant is never "not entitled" to their own credit ledger.
 *
 * ## Reach and Connect (slice 65)
 *
 * Until slice 65 "Reach" was every way a workspace talked to people — email and
 * WhatsApp campaigns, and from slice 40 Meta ads, which lived as a panel on the
 * Campaigns screen. The two halves follow different rules: on an owned channel
 * Wend holds the person and writes a receipt per message; on a paid one the
 * platform holds the people, the figures are aggregate and the money is a
 * budget. So they are two pillars now. **Reach** is paid acquisition — its one
 * section is `ads`, the workspace's Meta ads. **Connect** is owned channels —
 * its one section is `campaigns`, email and WhatsApp.
 *
 * Audiences, Segments and Templates used to sit under Reach because Reach was
 * the only pillar that used them. They are not Connect's either: Sense builds
 * its findings on segments, and filing them under one pillar would hide them
 * from a workspace that lacks it. They are the `shared` group — like
 * `workspace`, not a pillar and never "not entitled". The rejected alternative
 * was to fold them into `workspace`; that group is the workspace's machinery
 * (accounts, money, reports), and the people a workspace holds are the material
 * the pillars work on, not machinery.
 *
 * Slice 16 turns Integrations into a tab rail whose tabs are the extension
 * point for Flow's data sources; that lands inside the Integrations section and
 * changes nothing here.
 *
 * ## Flow is a group that says the software does not exist
 *
 * They are not links, not disabled links, and not locks with a price behind
 * them. {@link navPillarSchema} is a discriminated union on the pillar state
 * from `./overview.js`, and **only the `available` arm has a `sections`
 * field**. A `not_built` or `not_entitled` pillar has nowhere to put a
 * destination, so no component can render a link into software that does not
 * exist, and no future refactor can add one without changing this shape.
 *
 * The state itself is not recomputed here. {@link navPillar} calls
 * {@link overviewPillar} and uses its verdict, so the sidebar group and the
 * Overview's pillar card cannot disagree, and `not_built` keeps beating
 * `not_entitled` in one place rather than two. Slice 10's reasoning holds
 * unchanged: being sold something that does not exist is worse than being told
 * it is not on your plan, and "not built yet" stays true after they buy it.
 *
 * The rejected alternative was to give the unbuilt pillars their sections
 * anyway — Journeys, Triggers — greyed out, "so people can see what is coming".
 * That is a roadmap drawn as an interface. It generates support tickets from
 * people who click, and it dates badly the moment the plan changes; slice 4's
 * WhatsApp card is the precedent that reads as honest rather than unfinished,
 * and it did that by naming what is missing, not by drawing it.
 *
 * ## No route is added for a pillar that does not exist
 *
 * Nothing in this file names a path, so there is nothing here to add a route
 * for. (`ads` is a section id; `App.tsx` decides it lives at `/reach/ads`.) Stated explicitly because it is the obvious next commit: a `/flow` route
 * behind a "coming soon" page is a URL that will be shared, bookmarked and
 * indexed, and then has to keep working through the slice that actually builds
 * Flow.
 *
 * ## Position in the package
 *
 * A **leaf**. It imports `../enums.js` for the feature vocabulary and its
 * display order, and
 * `./overview.js` for the pillar state and its derivation; neither imports it
 * back, and nothing else imports this file. Slices 10, 12, 8 and 13 each
 * recorded the same trap — two modules that both build zod schemas at load and
 * import each other fail at *import*, with `z.enum(undefined)`, long before a
 * reviewer sees them — and the arrows here point one way only.
 *
 * Separate from `./onboarding.js` on purpose, though both are "the shell".
 * That one is a wire contract the API parses its own response against; this one
 * never leaves the browser. They have different owners, different lifetimes and
 * nothing to say to each other, and neither imports the other.
 */
import { z } from 'zod';

import { FEATURE_VALUES, PILLAR_ORDER, featureSchema, type Feature } from '../enums.js';
import { overviewPillar, type PillarState } from './overview.js';

/* -------------------------------------------------------------------------- */
/* Sections                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Every destination in the sidebar, by id.
 *
 * Ids, not labels and not paths — see the header. The list is exhaustive on
 * purpose: it is what makes the web app's label-and-route map a
 * `Record<NavSectionId, …>` that cannot silently miss a section.
 */
export const NAV_SECTION_VALUES = [
  'overview',
  'audiences',
  'segments',
  'templates',
  'campaigns',
  'integrations',
  'credits',
  'analytics',
  'reports',
  'today',
  'funnel',
  'pressure',
  'outcomes',
  'actions',
  // Slice 65: Reach's one destination, the workspace's Meta ads. Appended,
  // like Sense's five, because this list is a vocabulary and not the sidebar's
  // order — that is `PILLAR_SECTIONS` and `navigationTree`.
  'ads',
  // Slices 67–70: the Flow pillar's one destination, the field catalogue under
  // the shared Audiences group, and Reach's leads. Appended, as above.
  'flows',
  'fields',
  'leads',
] as const;
export const navSectionIdSchema = z.enum(NAV_SECTION_VALUES);
export type NavSectionId = z.infer<typeof navSectionIdSchema>;

/**
 * The one destination that sits above the groups.
 *
 * The Overview belongs to no pillar because it is the screen the pillars are
 * listed on, and because it is where a first-run workspace lands.
 */
export const NAV_HOME_SECTION = 'overview' as const satisfies NavSectionId;

/**
 * Which sections belong to which pillar.
 *
 * Reach is the workspace's Meta ads (slice 65 moved them off the Campaigns
 * screen). Connect is Campaigns — email and WhatsApp, the screen that gets a
 * message out of the door; the three screens that sat beside it under the old
 * Reach are {@link NAV_SHARED_SECTIONS} now. Sense opens on Today (slice 62) —
 * the ranked queue of things worth doing — then the Funnel (slice 60),
 * Pressure (slice 59), Outcomes (slice 60), and the Action ledger (slice 62),
 * which records what was done about each finding. Flow (slice 69) is its
 * Flows list — the builder opens from there — flipped together with
 * `PILLAR_IS_BUILT.flow` in `./overview.js`, as {@link navPillar} requires.
 * Reach gained Leads in slice 70: the people its click-to-WhatsApp ads brought
 * in, which land in Core and can start a flow. Until slice 69 this paragraph
 * said Flow's emptiness was the state of the repository; that is superseded.
 */
export const PILLAR_SECTIONS = {
  reach: ['ads', 'leads'],
  connect: ['campaigns'],
  flow: ['flows'],
  sense: ['today', 'funnel', 'pressure', 'outcomes', 'actions'],
} as const satisfies Record<Feature, readonly NavSectionId[]>;

/**
 * The layers every pillar draws on: not part of any pillar (slice 65).
 *
 * Audiences (the uploaded lists), Segments and Templates. Connect sends to a
 * segment with a template and Sense reads segments to find pressure and lift.
 * Filing them under any one pillar would hide them from a workspace without it
 * and suggest the others cannot use them. See the header for why they are not
 * folded into {@link NAV_WORKSPACE_SECTIONS}.
 */
export const NAV_SHARED_SECTIONS = [
  'audiences',
  // Slice 67: the field catalogue — every uploaded column, typed and corrected
  // — sits beside the uploads it is computed from.
  'fields',
  'segments',
  'templates',
] as const satisfies readonly [NavSectionId, ...NavSectionId[]];

/**
 * The workspace's own machinery: not part of any pillar.
 *
 * Integrations, Credits, Analytics and Reports serve whatever pillars a tenant
 * has. Filing them under a pillar would mean a copy of them per pillar, and it
 * would put two screens that answer questions about a period beside the
 * screens that send. `AppLayout.tsx` already made that argument for Analytics
 * and Reports; this preserves it. Analytics and Reports stayed here when slice
 * 65 split Reach from Connect: they compare surfaces across both.
 */
export const NAV_WORKSPACE_SECTIONS = [
  'integrations',
  'credits',
  'analytics',
  'reports',
] as const satisfies readonly [NavSectionId, ...NavSectionId[]];

/* -------------------------------------------------------------------------- */
/* Pillar groups                                                               */
/* -------------------------------------------------------------------------- */

/**
 * One arm per pillar state, and **only `available` may carry destinations**.
 *
 * `satisfies Record<PillarState, …>` is the load-bearing part: add a fourth
 * state to `OVERVIEW_PILLAR_STATE_VALUES` and this object stops compiling until
 * somebody decides whether that state may hold links. Without it, a new state
 * would fall through `navPillar` silently.
 */
const NAV_PILLAR_ARMS = {
  available: z
    .object({
      feature: featureSchema,
      state: z.literal('available'),
      /** Non-empty by construction: an available pillar you cannot enter is not available. */
      sections: z.array(navSectionIdSchema).nonempty(),
    })
    .strict(),
  not_built: z
    .object({ feature: featureSchema, state: z.literal('not_built') })
    .strict(),
  not_entitled: z
    .object({ feature: featureSchema, state: z.literal('not_entitled') })
    .strict(),
} satisfies Record<PillarState, z.ZodTypeAny>;

/**
 * A pillar as the sidebar renders it.
 *
 * `.strict()` on the two unbuilt arms is what stops a `sections` array being
 * attached to Flow "temporarily" — the object would fail to parse, rather than
 * quietly gaining links to screens that do not exist.
 */
export const navPillarSchema = z.discriminatedUnion('state', [
  NAV_PILLAR_ARMS.available,
  NAV_PILLAR_ARMS.not_built,
  NAV_PILLAR_ARMS.not_entitled,
]);
export type NavPillar = z.infer<typeof navPillarSchema>;

/**
 * Derives one pillar group.
 *
 * The state comes from {@link overviewPillar} and is not recomputed, so the
 * sidebar and the Overview's pillar card are the same verdict rendered twice
 * rather than two verdicts.
 *
 * The empty-sections branch is the one place {@link PILLAR_SECTIONS} and
 * `PILLAR_IS_BUILT` can disagree, and it resolves the disagreement in the
 * honest direction: a pillar marked built with nowhere to go has not shipped,
 * whatever the flag says, and an empty expandable group is a worse lie than
 * "not built yet". It is reachable only in the moment between flipping the flag
 * and adding the sections, and it fails towards not advertising.
 */
export function navPillar(feature: Feature, entitled: boolean): NavPillar {
  const { state } = overviewPillar(feature, entitled);
  if (state !== 'available') return { feature, state };

  const [first, ...rest] = PILLAR_SECTIONS[feature];
  if (first === undefined) return { feature, state: 'not_built' };

  return { feature, state: 'available', sections: [first, ...rest] };
}

/* -------------------------------------------------------------------------- */
/* The whole sidebar                                                           */
/* -------------------------------------------------------------------------- */

/**
 * The sidebar, top to bottom: home, then the four pillars in
 * {@link PILLAR_ORDER}, then the shared layers, then the workspace.
 *
 * `pillars` carries every feature including the unbuilt ones — an absent group
 * and a group that says "not built yet" are different statements, and only the
 * second one answers the question a person actually has. Which of them the
 * sidebar then *draws* is `AppLayout.tsx`'s rule, not this shape's: the tree
 * states every verdict, the renderer decides which verdicts earn a group.
 *
 * The refine checks the order against `PILLAR_ORDER`, the count against
 * `FEATURE_VALUES`, and that `PILLAR_ORDER` names each feature once. A label
 * missing from `PILLAR_ORDER` already fails to compile (`enums.ts`); the
 * distinct count is what catches the case a type cannot — a label written
 * twice, which keeps the length right and lets the per-index comparison pass.
 */
export const navigationTreeSchema = z
  .object({
    home: navSectionIdSchema,
    pillars: z.array(navPillarSchema),
    shared: z.array(navSectionIdSchema).nonempty(),
    workspace: z.array(navSectionIdSchema).nonempty(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const features = value.pillars.map((pillar) => pillar.feature);
    const complete =
      features.length === FEATURE_VALUES.length &&
      PILLAR_ORDER.length === FEATURE_VALUES.length &&
      new Set(PILLAR_ORDER).size === FEATURE_VALUES.length &&
      PILLAR_ORDER.every((feature, index) => features[index] === feature);

    if (!complete) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pillars'],
        message:
          'pillars must carry every feature exactly once, in PILLAR_ORDER — a pillar omitted because it is not built is a pillar nobody is told about',
      });
    }
  });
export type NavigationTree = z.infer<typeof navigationTreeSchema>;

/**
 * Builds the sidebar for one signed-in user.
 *
 * `features` is `SessionUser.features` from `GET /auth/me` — the tenant's
 * *enabled* features, so membership is the entitlement. Reach, Connect and
 * Sense are `available` for a tenant that has them; Flow comes back `not_built`
 * whether or not anybody bought it, which is `overviewPillar`'s rule and not a
 * second one. The shared layers and the workspace carry no state: every tenant
 * has them.
 */
export function navigationTree(features: readonly Feature[]): NavigationTree {
  return {
    home: NAV_HOME_SECTION,
    pillars: PILLAR_ORDER.map((feature) => navPillar(feature, features.includes(feature))),
    shared: [...NAV_SHARED_SECTIONS],
    workspace: [...NAV_WORKSPACE_SECTIONS],
  };
}
